<div class="main-content">
	<div class="container-fluid">
		<mat-card class="feature-surface">
			<mat-card-header>
				<div mat-card-avatar class="feature-surface__icon">
					<i class="material-icons" aria-hidden="true">request_quote</i>
				</div>
				<mat-card-title>Open Payables</mat-card-title>
				<mat-card-subtitle>
					Open supplier documents by invoice date, with an optional due-by cut-off
				</mat-card-subtitle>
			</mat-card-header>
			<mat-card-content>

				<form [formGroup]="filterForm" class="row mb-2" *ngIf="filterForm">
					<div class="col-6 col-md-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Invoiced from</mat-label>
							<input matInput type="date" formControlName="from">
						</mat-form-field>
					</div>
					<div class="col-6 col-md-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Invoiced to</mat-label>
							<input matInput type="date" formControlName="to">
						</mat-form-field>
					</div>
					<div class="col-6 col-md-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Due by</mat-label>
							<input matInput type="date" formControlName="dueby">
							<button matSuffix mat-icon-button type="button" *ngIf="filterForm.value.dueby"
								(click)="clearDueBy()" aria-label="Clear the due-by cut-off">
								<i class="material-icons" aria-hidden="true">close</i>
							</button>
							<mat-hint>Blank = all open</mat-hint>
						</mat-form-field>
					</div>
					<div class="col-12 col-md-3">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Vendor</mat-label>
							<mat-select formControlName="supplierno" multiple appSelectAll
								placeholder="All vendors with a balance">
								<mat-option *ngFor="let v of vendors" [value]="v.supplierno">
									{{ vendorLabel(v) }}
								</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-6 col-md-3">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Documents</mat-label>
							<mat-select formControlName="doctype">
								<mat-option value="all">Invoices &amp; credits</mat-option>
								<mat-option value="invoices">Invoices only</mat-option>
								<mat-option value="credits">Credits only</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-12 col-md-4">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Vendor invoice # / reference</mat-label>
							<input matInput formControlName="search" autocomplete="off"
								placeholder="Searches the whole range">
						</mat-form-field>
					</div>
					<div class="col-6 col-md-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Row cap</mat-label>
							<mat-select formControlName="limit">
								<mat-option *ngFor="let c of rowcaps" [value]="c">{{ c | number }} documents</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-6 col-md-3 d-flex align-items-center pb-3">
						<mat-checkbox formControlName="heldonly" color="accent">
							Held documents only
						</mat-checkbox>
					</div>
				</form>

				<!-- The three questions actually asked at a check run, without a date picker. -->
				<div class="op-presets" *ngIf="filterForm">
					<span class="op-presets__label">Quick pick</span>
					<button type="button" class="op-chip" *ngFor="let p of datePresets"
						[class.is-active]="isPresetActive(p)" (click)="applyPreset(p)">
						{{ p.label }}
					</button>
					<button type="button" class="op-chip is-clear" *ngIf="filterForm.value.dueby"
						(click)="clearDueBy()">
						<i class="material-icons" aria-hidden="true">close</i> Clear due-by
					</button>
				</div>

				<div class="d-flex mb-2 flex-wrap align-items-center" style="gap: 8px;">
					<button mat-flat-button color="accent" (click)="load()" [disabled]="loading">
						{{ loading ? 'Loading…' : 'Apply' }}
					</button>

					<!-- Which grid, and therefore what the exports contain. -->
					<div class="op-viewtoggle" role="group" aria-label="View">
						<button type="button" class="op-chip" [class.is-active]="view === 'vendor'"
							(click)="setView('vendor')">By vendor</button>
						<button type="button" class="op-chip" [class.is-active]="view === 'documents'"
							(click)="setView('documents')">All documents</button>
					</div>

					<button type="button" class="op-chip is-drill" *ngIf="activeBucket" (click)="clearBucket()">
						{{ bucketLabel(activeBucket) }}
						<i class="material-icons" aria-hidden="true">close</i>
					</button>

					<span class="flex-grow-1"></span>

					<mat-form-field appearance="outline" class="op-quick-search">
						<mat-label>Quick search</mat-label>
						<input matInput type="search" autocomplete="off" [formControl]="searcher">
						<i matSuffix class="material-icons" aria-hidden="true">search</i>
					</mat-form-field>

					<button mat-icon-button (click)="exportXls()" [disabled]="exporting || !hasExportableRows"
						matTooltip="Export to Excel" aria-label="Export to Excel">
						<i class="material-icons" aria-hidden="true">grid_on</i>
					</button>
					<button mat-icon-button (click)="exportPdf()" [disabled]="exporting || !hasExportableRows"
						matTooltip="Export to PDF" aria-label="Export to PDF">
						<i class="material-icons" aria-hidden="true">picture_as_pdf</i>
					</button>
					<mat-progress-spinner *ngIf="exporting" diameter="20" mode="indeterminate" color="accent">
					</mat-progress-spinner>
				</div>

				<div *ngIf="errorMessage" class="feature-section mb-3">
					<p class="feature-section__title m-0">{{ errorMessage }}</p>
				</div>

				<div *ngIf="loading" class="text-center my-4">
					<mat-progress-spinner diameter="32" mode="indeterminate" color="accent"></mat-progress-spinner>
				</div>

				<ng-container *ngIf="!loading">

					<div class="feature-tile-row" *ngIf="displayTotals">
						<app-kpi-tile tone="accent" label="documents"
							[value]="displayTotals.docs | number"
							[sub]="displayTotals.vendors + (displayTotals.vendors === 1 ? ' vendor' : ' vendors')">
						</app-kpi-tile>
						<app-kpi-tile tone="accent" label="open"
							[value]="displayTotals.open_balance | currency"></app-kpi-tile>
						<app-kpi-tile [tone]="isZero(displayTotals.past_due) ? 'settled' : 'failed'" label="past due"
							[value]="displayTotals.past_due | currency"></app-kpi-tile>
						<app-kpi-tile tone="settled" label="not yet due"
							[value]="displayTotals.not_due | currency"
							[sub]="undatedSub"></app-kpi-tile>
					</div>

					<!--
						Aging over the same filtered set. Buckets are days past DUE, not
						days since the invoice — a 60-day invoice is not late on day 45.
						Each row is a drill control; the strip keeps showing all of them
						after one is picked, or there would be no way back to the others.
					-->
					<div class="feature-section" *ngIf="displayBuckets.length > 1">
						<p class="feature-section__title">Aging &mdash; days past due</p>
						<div class="op-age">
							<button type="button" class="op-age__row" *ngFor="let b of displayBuckets"
								[class.is-active]="isBucketActive(b)" (click)="selectBucket(b)"
								[attr.aria-pressed]="isBucketActive(b)"
								[attr.title]="isBucketActive(b) ? 'Clear this filter' : 'Show only ' + b.label">
								<span class="op-age__label">{{ b.label }}</span>
								<span class="op-age__bar" aria-hidden="true">
									<span class="op-age__fill" [ngClass]="b.key"
										[style.width.%]="bucketShare(b)"></span>
								</span>
								<span class="op-age__docs">{{ b.docs | number }}</span>
								<span class="op-age__value">{{ b.open_balance | currency }}</span>
							</button>
						</div>
						<span class="feature-section__hint">{{ scopeLine }}</span>
					</div>

					<!-- ============================ BY VENDOR ============================ -->
					<ng-container *ngIf="view === 'vendor'">

						<p *ngIf="searching" class="text-muted mt-2 mb-1" style="font-size: 12px;">
							showing {{ vendorResults.length | number }} of {{ vendorRows.length | number }} vendors
						</p>

						<div class="feature-table-scroll">
							<table class="feature-table feature-table--responsive">
								<thead>
									<tr>
										<th class="op-expand-col noprint"></th>
										<th><button class="sort-head" (click)="vsortBy('vendor')">Vendor <i class="material-icons" aria-hidden="true">{{ vsortIcon('vendor') }}</i></button></th>
										<th><button class="sort-head" (click)="vsortBy('oldest')">Oldest due <i class="material-icons" aria-hidden="true">{{ vsortIcon('oldest') }}</i></button></th>
										<th class="is-num"><button class="sort-head" (click)="vsortBy('docs')">Docs <i class="material-icons" aria-hidden="true">{{ vsortIcon('docs') }}</i></button></th>
										<th>Aging</th>
										<th class="is-num"><button class="sort-head" (click)="vsortBy('pastdue')">Past due <i class="material-icons" aria-hidden="true">{{ vsortIcon('pastdue') }}</i></button></th>
										<th class="is-num"><button class="sort-head" (click)="vsortBy('open')">Open <i class="material-icons" aria-hidden="true">{{ vsortIcon('open') }}</i></button></th>
										<th class="noprint"></th>
									</tr>
								</thead>
								<tbody>
									<ng-container *ngFor="let v of vendorResults">
										<tr class="op-vendor" [class.is-open]="isVendorExpanded(v)">
											<td class="op-expand-col noprint">
												<button mat-icon-button (click)="toggleVendor(v)"
													[attr.aria-expanded]="isVendorExpanded(v)"
													[attr.aria-label]="'Documents for ' + v.suppname">
													<i class="material-icons" aria-hidden="true">
														{{ isVendorExpanded(v) ? 'expand_less' : 'expand_more' }}
													</i>
												</button>
											</td>
											<td data-label="Vendor">
												<span>
													<a [routerLink]="['/vendors', v.supplierno]" *ngIf="v.supplierno">{{ v.supplierno }}</a>
													<span *ngIf="!v.supplierno" class="op-blank">—</span>
													<div class="op-sub op-clamp" [title]="v.suppname">{{ v.suppname }}</div>
													<div class="text-muted op-sub" *ngIf="v.termsname">{{ v.termsname }}</div>
												</span>
											</td>
											<td data-label="Oldest due">
												<span *ngIf="v.oldest_due">{{ v.oldest_due | date:'MM/dd/yyyy' }}</span>
												<span *ngIf="!v.oldest_due" class="op-blank">—</span>
											</td>
											<td data-label="Docs" class="is-num">{{ v.docs | number }}</td>
											<td data-label="Aging">
												<span class="op-vbuckets">
													<span class="op-vbucket" *ngFor="let b of vendorBuckets(v)"
														[ngClass]="b.key" [title]="b.label">
														<em>{{ b.label }}</em>{{ b.amount | currency }}
													</span>
												</span>
											</td>
											<td data-label="Past due" class="is-num"
												[class.op-late]="!isZero(v.past_due)">{{ v.past_due | currency }}</td>
											<td data-label="Open" class="is-num op-open">{{ v.open_balance | currency }}</td>
											<td class="noprint">
												<button mat-stroked-button *ngIf="v.supplierno"
													(click)="payFromVendorRow(v)">Pay</button>
											</td>
										</tr>

										<tr class="op-drawer" *ngIf="isVendorExpanded(v)">
											<td colspan="8">
												<div class="text-center my-3" *ngIf="vendorDocsLoading">
													<mat-progress-spinner diameter="24" mode="indeterminate" color="accent">
													</mat-progress-spinner>
												</div>
												<ng-container *ngIf="!vendorDocsLoading">
													<p *ngIf="vendorDocsTruncated" class="text-muted mb-1"
														style="font-size: 12px;">
														Showing the first {{ vendorDocs.length | number }} documents for this vendor.
													</p>
													<ng-container
														*ngTemplateOutlet="docTable; context: { rows: vendorDocs, nested: true }">
													</ng-container>
												</ng-container>
											</td>
										</tr>
									</ng-container>

									<tr *ngIf="!vendorResults.length">
										<td colspan="8" class="text-center text-muted">
											Nothing open in this range.
										</td>
									</tr>

									<tr class="is-total" *ngIf="vendorResults.length && displayTotals">
										<td class="noprint op-blank"></td>
										<td colspan="3">{{ totalsLabel }}</td>
										<td></td>
										<td data-label="Past due" class="is-num">{{ displayTotals.past_due | currency }}</td>
										<td data-label="Open" class="is-num">{{ displayTotals.open_balance | currency }}</td>
										<td class="noprint"></td>
									</tr>
								</tbody>
							</table>
						</div>
					</ng-container>

					<!-- ========================== ALL DOCUMENTS ========================== -->
					<ng-container *ngIf="view === 'documents'">

						<p *ngIf="truncated && !searching" class="text-muted mt-2 mb-1" style="font-size: 12px;">
							Showing the first {{ limit | number }} documents &mdash; the tiles and totals cover the
							whole range, and an export always contains all of it. Raise the row cap to list more here.
						</p>
						<p *ngIf="searching" class="text-muted mt-2 mb-1" style="font-size: 12px;">
							showing {{ searchresults.length | number }} of {{ rows.length | number }} loaded documents
						</p>

						<ng-container *ngTemplateOutlet="docTable; context: { rows: searchresults, nested: false }">
						</ng-container>
					</ng-container>

				</ng-container>

				<!--
					Pay bar. A payment is to one supplier, so a selection spanning vendors
					has no single pay action — the button says which state it is in rather
					than quietly paying whichever vendor came first.
				-->
				<div class="op-paybar" *ngIf="selectedRows.length">
					<span class="op-paybar__count">
						{{ selectedRows.length | number }} selected
					</span>
					<span class="op-paybar__total">{{ selectedTotal | currency }}</span>
					<span class="flex-grow-1"></span>
					<button mat-stroked-button (click)="clearSelection()">Clear</button>
					<button mat-flat-button color="accent" [disabled]="!canPaySelected" (click)="paySelected()">
						{{ paySelectedLabel }}
					</button>
				</div>

			</mat-card-content>
		</mat-card>
	</div>
</div>

<!--
	One document grid, used both on its own and inside an expanded vendor row, so the
	two can never drift apart. `nested` drops the vendor column: inside a vendor's own
	drawer it would repeat the row above it on every line.
-->
<ng-template #docTable let-rows="rows" let-nested="nested">
	<div class="feature-table-scroll" [class.op-nested]="nested">
		<table class="feature-table feature-table--responsive">
			<thead>
				<tr>
					<th class="op-check-col noprint">
						<mat-checkbox color="accent" [checked]="allSelected(rows)"
							(change)="toggleAll(rows)" aria-label="Select all shown">
						</mat-checkbox>
					</th>
					<th *ngIf="!nested"><button class="sort-head" (click)="sortBy('vendor')">Vendor <i class="material-icons" aria-hidden="true">{{ sortIcon('vendor') }}</i></button></th>
					<th><button class="sort-head" (click)="sortBy('type')">Type <i class="material-icons" aria-hidden="true">{{ sortIcon('type') }}</i></button></th>
					<th><button class="sort-head" (click)="sortBy('doc')">Doc # <i class="material-icons" aria-hidden="true">{{ sortIcon('doc') }}</i></button></th>
					<th><button class="sort-head" (click)="sortBy('reference')">Reference <i class="material-icons" aria-hidden="true">{{ sortIcon('reference') }}</i></button></th>
					<th><button class="sort-head" (click)="sortBy('date')">Invoiced <i class="material-icons" aria-hidden="true">{{ sortIcon('date') }}</i></button></th>
					<th><button class="sort-head" (click)="sortBy('due')">Due <i class="material-icons" aria-hidden="true">{{ sortIcon('due') }}</i></button></th>
					<th><button class="sort-head" (click)="sortBy('age')">Age <i class="material-icons" aria-hidden="true">{{ sortIcon('age') }}</i></button></th>
					<th class="is-num"><button class="sort-head" (click)="sortBy('total')">Document total <i class="material-icons" aria-hidden="true">{{ sortIcon('total') }}</i></button></th>
					<th class="is-num">Allocated</th>
					<th class="is-num"><button class="sort-head" (click)="sortBy('open')">Open <i class="material-icons" aria-hidden="true">{{ sortIcon('open') }}</i></button></th>
				</tr>
			</thead>
			<tbody>
				<tr *ngFor="let r of rows" [class.is-picked]="isSelected(r)">
					<td class="op-check-col noprint">
						<mat-checkbox color="accent" [checked]="isSelected(r)" (change)="toggleRow(r)"
							[attr.aria-label]="'Select document ' + r.transno">
						</mat-checkbox>
					</td>
					<td data-label="Vendor" *ngIf="!nested">
						<span>
							<a [routerLink]="['/vendors', r.supplierno]" *ngIf="r.supplierno">{{ r.supplierno }}</a>
							<span *ngIf="!r.supplierno" class="op-blank">—</span>
							<div class="text-muted op-sub op-clamp" [title]="r.suppname">{{ r.suppname }}</div>
						</span>
					</td>
					<td data-label="Type">
						<span>{{ r.typename }}
							<div class="op-hold" *ngIf="r.hold">On hold</div>
						</span>
					</td>
					<td data-label="Doc #">{{ r.transno }}</td>
					<td data-label="Reference" class="op-clamp" [title]="r.suppreference">{{ r.suppreference }}</td>
					<td data-label="Invoiced">{{ r.trandate | date:'MM/dd/yyyy' }}</td>
					<td data-label="Due">
						<span *ngIf="r.duedate">{{ r.duedate | date:'MM/dd/yyyy' }}</span>
						<span *ngIf="!r.duedate" class="op-blank">—</span>
					</td>
					<td data-label="Age" [ngClass]="ageClass(r)">{{ ageLabel(r) }}</td>
					<td data-label="Document total" class="is-num">{{ r.totalamount | currency }}</td>
					<td data-label="Allocated" class="is-num"
						[class.op-zero]="isZero(r.allocated)">{{ r.allocated | currency }}</td>
					<td data-label="Open" class="is-num op-open">{{ r.open_balance | currency }}</td>
				</tr>

				<tr *ngIf="!rows.length">
					<td [attr.colspan]="nested ? 10 : 11" class="text-center text-muted">
						Nothing open in this range.
					</td>
				</tr>

				<tr class="is-total" *ngIf="rows.length && !nested && displayTotals">
					<td class="noprint op-blank"></td>
					<td colspan="7">{{ totalsLabel }}</td>
					<td data-label="Document total" class="is-num">{{ displayTotals.totalamount | currency }}</td>
					<td data-label="Allocated" class="is-num">{{ displayTotals.allocated | currency }}</td>
					<td data-label="Open" class="is-num">{{ displayTotals.open_balance | currency }}</td>
				</tr>
			</tbody>
		</table>
	</div>
</ng-template>
